import { useRef, useCallback, useEffect, useState } from 'react';

// Hook para gerenciar notificações sonoras de novos pedidos
export function useNewOrderNotification() {
  const [isEnabled, setIsEnabled] = useState(() => {
    const stored = localStorage.getItem('orderSoundEnabled');
    return stored !== null ? stored === 'true' : true;
  });
  const audioRef = useRef(null);
  const previousOrdersRef = useRef(new Set());
  const isFirstLoadRef = useRef(true);

  // Criar o elemento de áudio
  useEffect(() => {
    // Criar um AudioContext para som de notificação
    audioRef.current = new Audio();
    audioRef.current.volume = 0.7;
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Salvar preferência no localStorage
  useEffect(() => {
    localStorage.setItem('orderSoundEnabled', isEnabled.toString());
  }, [isEnabled]);

  // Função para tocar som de notificação usando Web Audio API
  const playNotificationSound = useCallback(() => {
    if (!isEnabled) return;
    
    try {
      // Usar Web Audio API para criar um som de notificação
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Criar um oscilador para gerar som
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Configurar o som - tipo "bell" style
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime); // A5
      oscillator.type = 'sine';
      
      // Volume decay
      gainNode.gain.setValueAtTime(0.5, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      // Tocar
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
      
      // Segundo tom (ding-dong)
      setTimeout(() => {
        const oscillator2 = audioContext.createOscillator();
        const gainNode2 = audioContext.createGain();
        
        oscillator2.connect(gainNode2);
        gainNode2.connect(audioContext.destination);
        
        oscillator2.frequency.setValueAtTime(659.25, audioContext.currentTime); // E5
        oscillator2.type = 'sine';
        
        gainNode2.gain.setValueAtTime(0.5, audioContext.currentTime);
        gainNode2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
        
        oscillator2.start(audioContext.currentTime);
        oscillator2.stop(audioContext.currentTime + 0.5);
      }, 200);
      
    } catch (error) {
      console.warn('Erro ao tocar som de notificação:', error);
    }
  }, [isEnabled]);

  // Função para verificar novos pedidos
  const checkNewOrders = useCallback((orders) => {
    if (!orders || orders.length === 0) return [];
    
    const currentOrderIds = new Set(orders.map(o => o.id));
    const newOrders = [];
    
    // Na primeira carga, apenas armazenar os IDs sem tocar som
    if (isFirstLoadRef.current) {
      previousOrdersRef.current = currentOrderIds;
      isFirstLoadRef.current = false;
      return [];
    }
    
    // Verificar se há novos pedidos
    orders.forEach(order => {
      if (!previousOrdersRef.current.has(order.id)) {
        newOrders.push(order);
      }
    });
    
    // Atualizar referência
    previousOrdersRef.current = currentOrderIds;
    
    // Se houver novos pedidos, tocar som
    if (newOrders.length > 0) {
      playNotificationSound();
    }
    
    return newOrders;
  }, [playNotificationSound]);

  // Função para resetar o estado (para quando mudar de página)
  const reset = useCallback(() => {
    previousOrdersRef.current = new Set();
    isFirstLoadRef.current = true;
  }, []);

  return {
    isEnabled,
    setIsEnabled,
    checkNewOrders,
    playNotificationSound,
    reset
  };
}
